import { createContext, useContext, useState } from "react";
import { Link } from "react-router";
import {
  BookOpen,
  ChevronDown,
  LogOut,
  MessageSquare,
  Monitor,
  Moon,
  Settings,
  Sun,
  Users,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
// 公開の頁もこの部品を通る (帯の HandIcon → luagate-ui の入口 → LgAccountMenu → ここ)。@noiman/ui の大きな入口を読むと、
// レッスンの頁の部品と react-resizable-panels まで公開の頁の束に入るので、絵だけの入口から読む
import { CreditCardIcon, GreenSenseiIcon } from "@noiman/ui/icons";
import { useLuagateTheme } from "../../contexts/luagate-theme.context";
import { useAuth } from "../../contexts/auth.context";
import { avatarInitials } from "../../lib/luagate/avatar-initials";

/**
 * アカウントの入口 (アバター) と、その中身のメニュー。
 *
 * サイトの上の帯 (LuagateHeader) と、帯を出さないページの左のサイドバーの両方が使う。
 * 中身を 1 か所にしておかないと、片方だけに項目が増えて割れる。
 */

/**
 * 設定の面の左ナビと同じ並び。
 * 増減したら components/settings/SettingsLayout.tsx と揃える
 */
export const SETTINGS_LINKS = [
  { label: "ユーザー情報", path: "/home" },
  { label: "パスワード", path: "/settings/password" },
  { label: "公開設定", path: "/settings/public" },
  { label: "連携", path: "/settings/connections" },
  { label: "目標の変更", path: "/settings/goal" },
] as const;

/*
 * 「支払い管理」(/settings/subscription) は、ゆめさくコードの Stripe (別のアカウント) の
 * サブスクの画面で、LuaGate の支払いは出ない。並べると取り違えるので、この面からは外した。
 * 画面そのものは残してある (設定の面の左ナビには今もある)。
 */

/**
 * 「お支払い情報」のリンク先 (Stripe のカスタマーポータルのログイン)。
 * null なら項目を出さない。体験の人と設定が空の場合の判定は layout が済ませて渡す
 * (lib/luagate/billing-portal.ts)。3 つの入口 (帯・サイドバー・引き出し) で同じ値を
 * 使うので、props を中継せず context にした
 */
export const LuagateBillingPortalContext = createContext<string | null>(null);

/** アバターの丸とレベルの札。押せる箱は呼ぶ側が持つ */
export function LuagateAvatar({
  userName,
  userPhotoUrl,
}: {
  userName?: string | null;
  userPhotoUrl?: string | null;
}) {
  const { user } = useAuth();
  const initials = avatarInitials(userName);
  const levelInfo = user?.levelInfo ?? null;
  return (
    <>
      <div className="w-full h-full rounded-full overflow-hidden">
        {userPhotoUrl ? (
          <img
            src={userPhotoUrl}
            alt={userName || "User"}
            className="w-full h-full object-cover"
          />
        ) : initials ? (
          <div className="w-full h-full bg-primary/20 flex items-center justify-center max-sm:text-[0.9375rem] text-sm font-bold text-primary">
            {initials}
          </div>
        ) : (
          // 名前も写真も無い人。ここを "?" にすると、設定していない
          // だけの人に「あなたが誰だか分からない」と言うことになる
          <div className="w-full h-full flex items-center justify-center bg-second">
            <GreenSenseiIcon variant="smile" size="M" />
          </div>
        )}
      </div>
      {/* レベル。アバターの外に出すので、親は overflow-visible のまま */}
      {levelInfo && (
        <span
          className="absolute -bottom-1 -right-1 min-w-[22px] rounded-full border-2 border-main bg-[var(--color-bg-green-chip)] px-1 max-sm:text-[0.9375rem] text-[10px] font-bold leading-[14px] max-sm:leading-[18px] text-[var(--color-text-on-green)]"
          title={`${levelInfo.rank} / ${levelInfo.totalXp.toLocaleString("ja-JP")} XP`}
        >
          {levelInfo.level}
        </span>
      )}
    </>
  );
}

/**
 * 「面談・相談」と「設定」の開き具合。popover を閉じても覚えておくよう、
 * popover の外 (開くボタンの側) で持つ
 */
export function useAccountMenuSections() {
  const [consultOpen, setConsultOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  return { consultOpen, setConsultOpen, settingsOpen, setSettingsOpen };
}

export type AccountMenuSections = ReturnType<typeof useAccountMenuSections>;

/** メニューの中身。popover でも、スマホの引き出しの中にそのまま並べても使う */
export function LuagateAccountMenuItems({
  userName,
  onNavigate,
  idPrefix,
  sections,
}: {
  userName?: string | null;
  /** 項目を押したとき (popover を閉じるなど) */
  onNavigate: () => void;
  /** 開閉の aria-controls を、同じページに 2 つ並んでも重ねないための頭 */
  idPrefix: string;
  sections: AccountMenuSections;
}) {
  const { theme, toggleTheme } = useLuagateTheme();
  const { user, logout } = useAuth();
  const levelInfo = user?.levelInfo ?? null;
  const billingPortalUrl = useContext(LuagateBillingPortalContext);
  const { consultOpen, setConsultOpen, settingsOpen, setSettingsOpen } = sections;
  return (
    <>
      {/* ユーザー情報。名前とレベルはここで見て、詳細は /home へ */}
      {(userName || levelInfo) && (
        <Link
          to="/home"
          onClick={() => onNavigate()}
          className="block px-3 py-2 border-b border-main mb-1 rounded-lg hover:bg-second/50 transition-colors"
        >
          {userName && (
            <div className="max-sm:text-[0.9375rem] text-sm font-bold text-main">
              {userName}
            </div>
          )}
          {levelInfo && (
            <>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-base font-bold text-main">
                  レベル {levelInfo.level}
                </span>
                <span className="max-sm:text-[0.9375rem] text-xs text-sub">
                  {levelInfo.rank}
                </span>
              </div>
              <div className="mt-1.5 h-1.5 w-full rounded-full bg-second">
                <div
                  className="h-1.5 rounded-full bg-primary transition-all duration-300"
                  style={{ width: `${levelInfo.progressPercent}%` }}
                />
              </div>
              <div className="mt-1 flex justify-between max-sm:text-[0.9375rem] text-[10px] text-sub">
                <span>{levelInfo.totalXp.toLocaleString("ja-JP")} XP</span>
                <span>次まで {levelInfo.xpToNextLevel.toLocaleString("ja-JP")} XP</span>
              </div>
            </>
          )}
        </Link>
      )}

      {/* 面談・相談（折りたたみ） */}
      <button
        onClick={() => setConsultOpen((v) => !v)}
        aria-expanded={consultOpen}
        aria-controls={`${idPrefix}-consult-submenu`}
        className="flex items-center justify-between w-full px-3 py-2 rounded-lg hover:bg-second/50 transition-colors"
      >
        <span className="flex items-center gap-2">
          <Monitor className="w-4 h-4 text-sub" />
          <span className="max-sm:text-[0.9375rem] text-sm text-main">面談・相談</span>
        </span>
        <motion.span
          animate={{ rotate: consultOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex"
        >
          <ChevronDown className="w-4 h-4 text-sub" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {consultOpen && (
          <motion.div
            id={`${idPrefix}-consult-submenu`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pl-4">
              <Link
                to="/luagate/mentoring"
                onClick={() => onNavigate()}
                className="block px-3 py-2 rounded-lg hover:bg-second/50 transition-colors max-sm:text-[0.9375rem] text-sm text-main"
              >
                1on1 面談
              </Link>
              <Link
                to="/luagate/mentoring?tab=goal-sheet"
                onClick={() => onNavigate()}
                className="block px-3 py-2 rounded-lg hover:bg-second/50 transition-colors max-sm:text-[0.9375rem] text-sm text-main"
              >
                目標シート
              </Link>
              <Link
                to="/luagate/mentoring?tab=monthly-report"
                onClick={() => onNavigate()}
                className="block px-3 py-2 rounded-lg hover:bg-second/50 transition-colors max-sm:text-[0.9375rem] text-sm text-main"
              >
                月報
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 設定（折りたたみ）。設定の面の左ナビと同じ並び。
          毎回サイドバーから設定ページへ行くのが手間なので、
          アバターメニューからそのまま飛べるようにしてある */}
      <button
        onClick={() => setSettingsOpen((v) => !v)}
        aria-expanded={settingsOpen}
        aria-controls={`${idPrefix}-settings-submenu`}
        className="flex items-center justify-between w-full px-3 py-2 rounded-lg hover:bg-second/50 transition-colors"
      >
        <span className="flex items-center gap-2">
          <Settings className="w-4 h-4 text-sub" />
          <span className="max-sm:text-[0.9375rem] text-sm text-main">設定</span>
        </span>
        <motion.span
          animate={{ rotate: settingsOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex"
        >
          <ChevronDown className="w-4 h-4 text-sub" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {settingsOpen && (
          <motion.div
            id={`${idPrefix}-settings-submenu`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pl-4">
              {SETTINGS_LINKS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => onNavigate()}
                  className="block px-3 py-2 rounded-lg hover:bg-second/50 transition-colors max-sm:text-[0.9375rem] text-sm text-main"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* お支払い情報。Stripe のカスタマーポータルは外のサイトなので別タブで開く */}
      {billingPortalUrl && (
        <a
          href={billingPortalUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onNavigate()}
          data-testid="luagate-billing-portal-link"
          className="flex items-start gap-2 px-3 py-2 rounded-lg hover:bg-second/50 transition-colors"
        >
          <CreditCardIcon className="w-4 h-4 mt-0.5 shrink-0 text-sub" color="currentColor" />
          <span className="min-w-0">
            <span className="block max-sm:text-[0.9375rem] text-sm text-main">お支払い情報</span>
            <span className="block max-sm:text-[0.9375rem] text-[11px] leading-snug text-sub">
              カードの変更や、月払いと 2 回に分けてのお支払いの確認
            </span>
          </span>
        </a>
      )}

      {/* ゆめさくの歩き方（manifesto） */}
      <Link
        to="/luagate/articles/manifesto"
        onClick={() => onNavigate()}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-second/50 transition-colors"
      >
        <BookOpen className="w-4 h-4 text-sub" />
        <span className="max-sm:text-[0.9375rem] text-sm text-main">ゆめさくの歩き方</span>
      </Link>

      {/* テーマ設定 */}
      <button
        onClick={toggleTheme}
        className="flex items-center justify-between w-full px-3 py-2 rounded-lg hover:bg-second/50 transition-colors"
      >
        <span className="flex items-center gap-2">
          {theme === "dark" ? (
            <Sun className="w-4 h-4 text-sub" />
          ) : (
            <Moon className="w-4 h-4 text-sub" />
          )}
          <span className="max-sm:text-[0.9375rem] text-sm text-main">テーマ設定</span>
        </span>
        <span className="max-sm:text-[0.9375rem] text-xs text-sub">
          {theme === "dark" ? "ダークモード" : "ライトモード"}
        </span>
      </button>

      {/* 紹介プログラム */}
      <Link
        to="/luagate/referral"
        onClick={() => onNavigate()}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-second/50 transition-colors"
      >
        <Users className="w-4 h-4 text-primary" />
        <span className="max-sm:text-[0.9375rem] text-sm text-main">紹介プログラム</span>
      </Link>

      {/* ご意見箱 — ヘッダーに出していたが、常設するほど押されないので
          アバターメニューへ移した。外部フォームなので別タブで開く */}
      <a
        href="https://form.asana.com/?k=_N5U4jhy8Kh4BR7F1NDaEA&d=1206397465633629"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onNavigate()}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-second/50 transition-colors"
        title="ご意見・ご要望を送る"
      >
        <MessageSquare className="w-4 h-4 text-sub" />
        <span className="max-sm:text-[0.9375rem] text-sm text-main">ご意見箱</span>
      </a>

      {/* ログアウト */}
      <div className="border-t border-main mt-1 pt-1">
        <button
          onClick={async () => {
            onNavigate();
            await logout();
            window.location.href = "/";
          }}
          className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-second/50 transition-colors w-full text-left"
        >
          <LogOut className="w-4 h-4 text-sub" />
          <span className="max-sm:text-[0.9375rem] text-sm text-main">ログアウト</span>
        </button>
      </div>
    </>
  );
}
